import React, { useState, useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import { FaPen, FaTrash, FaHandPaper, FaMagic } from 'react-icons/fa';
import { toast } from 'react-toastify';
import UpdateDiscount from './components/UpdateDiscount/UpdateDisocunt';
import DeleteDiscount from './components/DeleteDiscount/DeleteDiscount';
import {
  getMenuOptions,
  getItemOptions,
  getCategoryOptions,
  createOptionMap,
} from '../utils';
import { changePaymentDiscountStatusById } from '../../../../../../crud/payments_api/payment_discounts';
import { formatCurrency, getPermissionState } from '../../../../../../utils/functions';
import CreateDiscount from './components/CreateDiscount/CreateDiscount';
import './Descuentos.scss';

interface DiscountsProps {
  descuentos: any[];
  setDescuentos: (descuentos: any[]) => void;
  showCreateDiscountModal: boolean;
  showUpdateDiscountModal: boolean;
  setShowCreateDiscountModal: (show: boolean) => void;
  setShowUpdateDiscountModal: (show: boolean) => void;
  dynamicallySelectedDiscounts: any[];
  toggleDiscountSelection: (discount: any) => void;
  selectionMode: boolean; // Nueva propiedad para el modo de selección
}

const Discounts: React.FC<DiscountsProps> = ({
  descuentos,
  setDescuentos,
  showCreateDiscountModal,
  showUpdateDiscountModal,
  setShowCreateDiscountModal,
  setShowUpdateDiscountModal,
  dynamicallySelectedDiscounts,
  toggleDiscountSelection,
  selectionMode, // Se recibe la propiedad
}) => {
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [menuOptions, setMenuOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [itemMap, setItemMap] = useState<any>({});
  const [menuMap, setMenuMap] = useState<any>({});
  const [categoryMap, setCategoryMap] = useState<any>({});

  // Estados para el modal de eliminación
  const [showDeleteDiscountModal, setShowDeleteDiscountModal] = useState<boolean>(false);
  const [discountToDelete, setDiscountToDelete] = useState<any | null>(null);
  const [selectedDiscount, setSelectedDiscount] = useState<any | null>(null);

  useEffect(() => {
    getMenuOptions().then((response) => {
      if (response) {
        setMenuOptions(response);
        setMenuMap(createOptionMap(response));
      }
    });
    getCategoryOptions().then((response) => {
      if (response) {
        setCategoryOptions(response);
        setCategoryMap(createOptionMap(response));
      }
    });
    getItemOptions().then((response) => {
      if (response) {
        setItemOptions(response);
        setItemMap(createOptionMap(response));
      }
    });
  }, []);

  const handleStatusChange = async (discount: any) => {
    const newStatus = !discount.isvalid;

    try {
      await changePaymentDiscountStatusById(discount.payment_discount_id);

      const updatedDiscounts = descuentos.map((d) =>
        d.payment_discount_id === discount.payment_discount_id
          ? { ...d, isvalid: newStatus }
          : d
      );
      setDescuentos(updatedDiscounts);
      toast.success('¡Estado del descuento actualizado correctamente!');
    } catch (error) {
      toast.error('Hubo un error al actualizar el estado del descuento.');
      console.error('Error cambiando el estado del descuento:', error);
    }
  };

  function formatDateTime(date: string) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('es-ES', { timeZone: 'UTC' });
  }

  function getDiscountScope(discount: any) {
    // Use enriched detail fields if available
    if (discount.scope_type === 'global' || discount.isglobal) {
      return 'En cualquier item';
    }
    
    if (discount.scope_type === 'menu' && discount.menu_detail) {
      return `En items del menú ${discount.menu_detail.menu_name}`;
    }
    
    if ((discount.scope_type === 'item' || discount.scope_type === 'multi_item') && discount.items_detail && discount.items_detail.length > 0) {
      if (discount.items_detail.length === 1) {
        return `En ${discount.items_detail[0].item_name}`;
      }
      return `En ${discount.items_detail.length} ítems: ${discount.items_detail.map((item: any) => item.item_name).join(', ')}`;
    }
    
    if ((discount.scope_type === 'category' || discount.scope_type === 'multi_category') && discount.categories_detail && discount.categories_detail.length > 0) {
      if (discount.categories_detail.length === 1) {
        return `En items de la categoría ${discount.categories_detail[0].category_name}`;
      }
      return `En ${discount.categories_detail.length} categorías: ${discount.categories_detail.map((cat: any) => cat.category_name).join(', ')}`;
    }
    
    // Fallback to legacy logic
    if (discount.menu_id) return `En items del menu ${menuMap[discount.menu_id]}`;
    if (discount.menu_category_id) return `En items de la categoría ${categoryMap[discount.menu_category_id]}`;
    if (discount.item_id) return `En ${itemMap[discount.item_id]}`;
    
    return 'Local';
  }

  function getAmountOrPercentage(discount: any) {
    if (discount.payment_discount_amount) return formatCurrency(discount.payment_discount_amount);
    if (discount.payment_discount_percentage) return `${discount.payment_discount_percentage}%`;
    return 'No especificado';
  }

  function getLimitConditions(discount: any) {
    if (discount.expiration_date && discount.max_budget) {
      return (
        <div className="discount-limit-conditions">
          <p className="discount-limit-text">
            {formatCurrency(discount.current_amount_used)} usado de{' '}
            {formatCurrency(discount.max_budget)}
          </p>
          <p className="discount-limit-text">Ó hasta {formatDateTime(discount.expiration_date)}</p>
        </div>
      );
    }
    if (!discount.expiration_date && discount.max_budget) {
      return (
        <div className="discount-limit-conditions">
          <p className="discount-limit-text">
            {formatCurrency(discount.current_amount_used)} usado de{' '}
            {formatCurrency(discount.max_budget)}
          </p>
        </div>
      );
    }
    if (discount.expiration_date && !discount.max_budget) {
      return (
        <div className="discount-limit-conditions">
          <p className="discount-limit-text">Valido hasta {formatDateTime(discount.expiration_date)}</p>
        </div>
      );
    }
    if (discount.max_number) {
      return (
        <div className="discount-limit-conditions">
          <p className="discount-limit-text">
            Utilizados: {discount.current_number_used} de {discount.max_number}
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <>
      <div className="descuentos-container">
        {descuentos.map((discount, index) => (
          <Card
            key={index}
            className={`discount-card ${dynamicallySelectedDiscounts.includes(discount) ? 'selected' : ''} ${!discount.isvalid ? 'disabled' : ''}`}
            onClick={() => {
              if (selectionMode) {
                toggleDiscountSelection(discount);
              }
            }}
          >
            <Card.Body>
              {/* Top Row: Badge and Toggle */}
              <div className="discount-card-top-row">
                <div className={`application-mode-badge ${discount.is_manual ? 'manual' : 'automatic'}`}>
                  {discount.is_manual ? (
                    <>
                      <FaHandPaper className="badge-icon" />
                      <span>Manual</span>
                    </>
                  ) : (
                    <>
                      <FaMagic className="badge-icon" />
                      <span>Automático</span>
                    </>
                  )}
                </div>
                <div className="status-toggle">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={discount.isvalid}
                      onChange={() => handleStatusChange(discount)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              {/* Main Content */}
              <div className="discount-card-main">
                <Card.Title className="discount-card-title">
                  {discount.payment_discount_name}
                </Card.Title>
                <p className="discount-amount-percentage">{getAmountOrPercentage(discount)}</p>
                <p className="discount-scope-text">{getDiscountScope(discount)}</p>
              </div>
            </Card.Body>

            {getLimitConditions(discount)}

            <Card.Footer className="discount-card-footer">
              <div className="discount-stats-row">
                <div className="discount-stat">
                  <span className="stat-value">{discount.current_number_used}</span>
                  <span className="stat-label">descuento usado</span>
                </div>
                <div className="discount-stat">
                  <span className="stat-value">{formatCurrency(discount.current_amount_used)}</span>
                  <span className="stat-label">usados</span>
                </div>
              </div>
              
              {!selectionMode && (
                <div className="discount-action-buttons">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDiscount(discount);
                      setShowUpdateDiscountModal(true);
                    }}
                    className="action-button edit-btn"
                    variant="none"
                    disabled={!getPermissionState(99)}
                  >
                    <FaPen />
                  </Button>
                  <Button
                    className="action-button delete-btn"
                    variant="none"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDiscountToDelete(discount);
                      setShowDeleteDiscountModal(true);
                    }}
                    disabled={!getPermissionState(100)}
                  >
                    <FaTrash />
                  </Button>
                </div>
              )}
            </Card.Footer>
          </Card>
        ))}
      </div>

      {/* Crear Descuento */}
      <CreateDiscount
        show={showCreateDiscountModal}
        handleClose={() => setShowCreateDiscountModal(false)}
        setDiscount={setDescuentos}
        discounts={descuentos}
        itemOptions={itemOptions}
        menuOptions={menuOptions}
        categoryOptions={categoryOptions}
        setShowCreateDiscountModal={setShowCreateDiscountModal}
      />

      {/* Editar Descuento */}
      <UpdateDiscount
        show={showUpdateDiscountModal}
        handleClose={() => setShowUpdateDiscountModal(false)}
        discounts={descuentos}
        setDiscount={setDescuentos}
        discount={selectedDiscount}
      />

      {/* Modal de Eliminación */}
      <DeleteDiscount
        show={showDeleteDiscountModal}
        handleClose={() => setShowDeleteDiscountModal(false)}
        discountsToDelete={discountToDelete ? [discountToDelete] : []}
        setDiscounts={setDescuentos}
      />
    </>
  );
};

export default Discounts;
